/* eslint-disable @typescript-eslint/no-explicit-any */

// Utility to convert various files (PDF, DOCX, TXT/JSON/XML/HTML, images)
// into an array of PNG data URLs suitable for sending to vision models.
// Libraries are loaded at runtime from CDN only when required to avoid bundling.

const CDN = {
  pdfjs: {
    lib: "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js",
    worker: "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js",
    global: "pdfjsLib",
  },
  html2canvas: {
    lib: "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js",
    global: "html2canvas",
  },
  mammoth: {
    lib: "https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js",
    global: "mammoth",
  },
};

function loadScriptOnce(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load script ${src}`));
    document.head.appendChild(s);
  });
}

async function ensurePdfJs(): Promise<any> {
  await loadScriptOnce(CDN.pdfjs.lib);
  const w = window as any;
  if (!w[CDN.pdfjs.global]) throw new Error("pdfjsLib not available");
  // set worker
  try {
    w[CDN.pdfjs.global].GlobalWorkerOptions.workerSrc = CDN.pdfjs.worker;
  } catch {}
  return w[CDN.pdfjs.global];
}

async function ensureHtml2Canvas(): Promise<any> {
  await loadScriptOnce(CDN.html2canvas.lib);
  const w = window as any;
  if (!w[CDN.html2canvas.global]) throw new Error("html2canvas not available");
  return w[CDN.html2canvas.global];
}

async function ensureMammoth(): Promise<any> {
  await loadScriptOnce(CDN.mammoth.lib);
  const w = window as any;
  if (!w[CDN.mammoth.global]) throw new Error("mammoth not available");
  return w[CDN.mammoth.global];
}

function createHiddenBuffer(width = 800): HTMLElement {
  const buffer = document.createElement("div");
  buffer.style.position = "absolute";
  buffer.style.left = "-9999px";
  buffer.style.top = "0";
  buffer.style.width = `${width}px`;
  buffer.style.background = "#2c2c2c";
  buffer.style.padding = "40px";
  buffer.style.lineHeight = "1.6";
  document.body.appendChild(buffer);
  return buffer;
}

function cleanupBuffer(el: HTMLElement) {
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

export type ConvertedImage = {
  dataUrl: string;
  label?: string;
};

export async function getConvertibleFilePageCount(file: File): Promise<number> {
  const ext = (file.name.split(".").pop() || "").toLowerCase();

  // Images count as 1
  if (["png", "jpg", "jpeg", "webp", "gif", "bmp"].includes(ext) || file.type.startsWith("image/")) {
    return 1;
  }

  // PDF can be counted without rendering
  if (ext === "pdf" || file.type === "application/pdf") {
    const pdfjs = await ensurePdfJs();
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    return Number(pdf?.numPages || 1);
  }

  // For docx/text/etc we currently generate a single image
  if (ext === "docx" || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
    return 1;
  }

  if (["txt", "json", "xml", "html", "css", "js", "md"].includes(ext) || file.type.startsWith("text/")) {
    return 1;
  }

  // Unknown: assume 1
  return 1;
}

export async function convertFileToImages(file: File): Promise<ConvertedImage[]> {
  const ext = (file.name.split(".").pop() || "").toLowerCase();

  if (["png", "jpg", "jpeg", "webp", "gif", "bmp"].includes(ext) || file.type.startsWith("image/")) {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    return [{ dataUrl, label: "Image" }];
  }

  if (ext === "pdf" || file.type === "application/pdf") {
    const pdfjs = await ensurePdfJs();
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    const results: ConvertedImage[] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) continue;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: ctx, viewport }).promise;
      results.push({ dataUrl: canvas.toDataURL("image/png"), label: `Page ${i}` });
    }
    return results;
  }

  if (ext === "docx" || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
    const [mammoth, html2canvas] = await Promise.all([ensureMammoth(), ensureHtml2Canvas()]);
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.convertToHtml({ arrayBuffer });
    const buffer = createHiddenBuffer();
    buffer.innerHTML = result.value;
    const canvas = await html2canvas(buffer, { scale: 2 });
    const dataUrl = canvas.toDataURL("image/png");
    cleanupBuffer(buffer);
    return [{ dataUrl, label: "Document" }];
  }

  if (["txt", "json", "xml", "html", "css", "js", "md"].includes(ext) || file.type.startsWith("text/")) {
    const html2canvas = await ensureHtml2Canvas();
    const text = await file.text();
    const buffer = createHiddenBuffer();
    const pre = document.createElement("pre");
    pre.style.whiteSpace = "pre-wrap";
    pre.style.wordWrap = "break-word";
    pre.textContent = text;
    buffer.appendChild(pre);
    const canvas = await html2canvas(buffer, { scale: 2 });
    const dataUrl = canvas.toDataURL("image/png");
    cleanupBuffer(buffer);
    return [{ dataUrl, label: "Text" }];
  }

  // Fallback: try to read as data URL; if not possible, throw.
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  return [{ dataUrl, label: "File" }];
}
